// Grocery header. Two-row supermarket chrome:
//   1) Masthead    — green row: logo · search · deliver-to (store selector) ·
//                    wishlist · My Account · My Cart
//   2) Nav bar     — primary links
// Phones get app-style chrome: deliver-to chip · favourites · avatar, a
// rounded search pill, and a bottom tab bar (the cart
// is the floating cart bar).
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Avatar, Badge, Grid, Dropdown } from 'antd';
import {
  AppstoreOutlined,
  CloseOutlined,
  DownOutlined,
  HeartOutlined,
  HomeOutlined,
  LogoutOutlined,
  ReadOutlined,
  SearchOutlined,
  ShoppingOutlined,
  UserOutlined,
  // UserSwitchOutlined, // used by the hidden Switch Account item
} from '@ant-design/icons';
import { Link, NavLink, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '@core/hooks/useAuth';
import { useAccountSelectionStore } from '@core/store/accountSelectionStore';
import { useActiveCart } from '@core/hooks/useCart';
import { useLocations } from '@core/hooks/useLocations';
import { useWishlistStore } from '@core/store/wishlistStore';
import { useStoreContext } from '@core/hooks/useStoreContext';
import { useUiStore } from '@core/store/uiStore';
import { useRuntimeBrand } from '@core/hooks/useRuntimeBrand';
import { useProfile } from '@core/hooks/useAccount';
import { getAvatarStyles } from '@core/utils/avatar';
import CartDrawer from '@components/base/CartDrawer';
import CheckoutModal from '@components/base/CheckoutModal';
import {
  CHECKOUT_MODAL_REDIRECT,
  loadCheckoutModule,
  openCheckoutModal,
} from '@components/base/CheckoutModal/checkoutModalEvents.js';
import StorefrontAuthModal from '@components/base/StorefrontAuthModal';
import BrandLogo from '@components/base/BrandLogo';
import Price from '@components/base/Price';
import { useIndustry } from '@/app/providers/industry-context.js';
import { useAppLanguage } from '@/app/providers/useAppLanguage.js';
import { useCopy } from '@/app/providers/useCopy';

// Type pair for the grocery skin; injected once, grocery only.
const GROCERY_FONTS_ID = 'grocery-skin-fonts';
const GROCERY_FONTS_HREF =
  'https://fonts.googleapis.com/css2?family=Courgette&family=Jost:wght@400;500;600;700&family=Poppins:wght@400;500;600;700&display=swap';

function useGroceryFonts() {
  useEffect(() => {
    if (document.getElementById(GROCERY_FONTS_ID)) return;
    const link = document.createElement('link');
    link.id = GROCERY_FONTS_ID;
    link.rel = 'stylesheet';
    link.href = GROCERY_FONTS_HREF;
    document.head.appendChild(link);
  }, []);
}

const fallbackNav = [
  { label: 'Shop', to: '/catalog' },
  { label: 'Categories', to: '/catalog?view=categories' },
  { label: 'My Orders', to: '/orders' },
];

// Solid red map pin with a white dot (deliver-to store picker).
function LocationPin() {
  return (
    <svg className="gx-location__pin" viewBox="0 0 24 24" aria-hidden focusable="false">
      <path
        fill="#f0443a"
        d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7z"
      />
      <circle cx="12" cy="9" r="2.6" fill="#fff" />
    </svg>
  );
}

// Store name stacked: first word large, the rest as small spaced capitals
// underneath ("REDWAY" over "G R O C E R Y"). A one-word name shows alone.
function BrandName({ appName }) {
  const [main, ...rest] = appName.trim().split(/\s+/);
  const sub = rest.join(' ');
  return (
    <span className={`gx-brand__name ${sub ? 'is-stacked' : ''}`}>
      <span className="gx-brand__name-main">{main}</span>
      {sub ? <span className="gx-brand__name-sub">{sub}</span> : null}
    </span>
  );
}

function BrandMark({ logoUrl, appName }) {
  return (
    <Link to="/" className="gx-brand" aria-label={appName || 'Home'}>
      {logoUrl ? (
        <BrandLogo src={logoUrl} className="gx-brand__logo" />
      ) : (
        <span className="gx-brand__logo gx-brand__logo--mark" aria-hidden>
          <ShoppingOutlined />
        </span>
      )}
      {appName ? <BrandName appName={appName} /> : null}
    </Link>
  );
}

export default function HeaderGrocery() {
  const t = useCopy();
  useGroceryFonts();
  useAppLanguage();
  const { config } = useIndustry();
  const { logoUrl: runtimeLogoUrl, brandName: runtimeBrandName } = useRuntimeBrand();
  const { customer, isAuthenticated, logout } = useAuth();
  const { cart, lineCount } = useActiveCart();
  const wishlistCount = useWishlistStore((s) => s.items.length);
  const { data: locations = [] } = useLocations();
  const { locationId } = useStoreContext();
  const openStoreSelector = useUiStore((s) => s.openStoreSelector);
  const openAccountSelector = useAccountSelectionStore((s) => s.openAccountSelector);
  const profileQuery = useProfile();
  const navigate = useNavigate();
  const location = useLocation();
  const { pathname, search } = location;
  const screens = Grid.useBreakpoint();
  const isMobile = !screens.md;

  const headerRef = useRef(null);
  const mastheadRef = useRef(null);
  // True once the masthead has scrolled away and only the sticky nav bar
  // remains at the top of the screen.
  const [isStuck, setIsStuck] = useState(false);
  const searchTimeoutRef = useRef(null);
  const [cartOpen, setCartOpen] = useState(false);
  const [authMode, setAuthMode] = useState(null);
  const [authRedirect, setAuthRedirect] = useState(null);
  const [searchText, setSearchText] = useState('');
  const [mobileSearchOpen, setMobileSearchOpen] = useState(false);

  const searchParams = useMemo(() => new URLSearchParams(search), [search]);
  const isCategoryView = searchParams.get('view') === 'categories';
  const isCatalogPage = pathname === '/catalog' || pathname.startsWith('/catalog');
  // "All Products" catalog page (catalog without view=categories) has its own toolbar search.
  // Hide navbar search on All Products page, but keep it on Home, Categories, My Orders, etc.
  const isAllProductsPage = isCatalogPage && !isCategoryView;
  const isSearchAllowedPage = !isAllProductsPage;

  const appName = runtimeBrandName || config?.copy?.appName || config?.name || '';
  const logoUrl = runtimeLogoUrl || config?.logoUrl || '';
  const displayName =
    profileQuery.data?.first_name ||
    customer?.first_name ||
    customer?.email?.split('@')[0] ||
    t('account', 'Account');

  const selectedLocation = locations.find((loc) => loc.id === locationId);
  const selectedLocationLabel =
    selectedLocation?.name ||
    [selectedLocation?.city, selectedLocation?.state].filter(Boolean).join(', ') ||
    '';

  const primaryNav = config?.nav?.primary?.length ? config.nav.primary : fallbackNav;

  // Publish the header height so sticky page chrome (catalog sidebar, cart
  // summary) can sit just below it.
  useEffect(() => {
    const el = headerRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return undefined;
    const observer = new ResizeObserver(([entry]) => {
      document.documentElement.style.setProperty(
        '--grocery-header-height',
        `${entry.target.offsetHeight}px`,
      );
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const el = mastheadRef.current;
    if (!el || typeof IntersectionObserver === 'undefined') return undefined;
    // -1px top margin: once stuck the masthead's bottom edge sits exactly on
    // the viewport top, which would still count as intersecting.
    const observer = new IntersectionObserver(([entry]) => setIsStuck(!entry.isIntersecting), {
      rootMargin: '-1px 0px 0px 0px',
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, [isMobile]);

  useEffect(() => () => clearTimeout(searchTimeoutRef.current), []);

  // Keep the box in step with ?q= when the catalog drives the search.
  // Leaving the catalog (to a product, Home, …) ends the search: start the
  // next page with an empty box.
  useEffect(() => {
    if (isCatalogPage) return;
    clearTimeout(searchTimeoutRef.current);
    setSearchText('');
  }, [isCatalogPage, pathname]);

  useEffect(() => {
    if (!isCatalogPage) return;
    const q = new URLSearchParams(search).get('q') ?? '';
    setSearchText((current) => (current.trim() === q.trim() ? current : q));
  }, [isCatalogPage, search]);

  useEffect(() => {
    if (!location.state?.openCartDrawer) return;
    setCartOpen(true);
    navigate(`${pathname}${search}`, { replace: true, state: null });
  }, [location.state, navigate, pathname, search]);

  useEffect(() => {
    const handleOpenAuthModal = (e) => {
      const { mode = 'login', redirect = null } = e.detail || {};
      setAuthMode(mode);
      if (redirect) setAuthRedirect(redirect);
    };
    window.addEventListener('open-auth-modal', handleOpenAuthModal);
    return () => window.removeEventListener('open-auth-modal', handleOpenAuthModal);
  }, []);

  useEffect(() => {
    setMobileSearchOpen(false);
  }, [pathname]);

  useEffect(() => {
    if (location.state?.focusSearch) {
      setMobileSearchOpen(true);
    }
  }, [location.key, location.state]);

  useEffect(() => {
    if (!mobileSearchOpen) return;
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        setMobileSearchOpen(false);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [mobileSearchOpen]);

  // focusSearch: All Products hides this box, so hand focus to its toolbar
  // search and the shopper keeps typing there.
  const runSearch = (value, { focusSearch = false } = {}) => {
    if (!isSearchAllowedPage) return;
    const q = value.trim();
    navigate(q ? `/catalog?q=${encodeURIComponent(q)}` : '/catalog', {
      state: focusSearch ? { focusSearch: true } : undefined,
    });
  };

  // Live search from any page (like the restaurant header): results open
  // 300ms after the shopper stops typing.
  const onSearchChange = (event) => {
    const value = event.target.value;
    setSearchText(value);
    clearTimeout(searchTimeoutRef.current);
    if (!value.trim() && !isCatalogPage) return;
    searchTimeoutRef.current = setTimeout(() => runSearch(value, { focusSearch: true }), 300);
  };

  const onSearchSubmit = (event) => {
    event.preventDefault();
    clearTimeout(searchTimeoutRef.current);
    setMobileSearchOpen(false);
    runSearch(searchText);
  };

  const requireAuth = (redirect) => {
    if (isAuthenticated) {
      navigate(redirect);
      return;
    }
    setAuthRedirect(redirect);
    setAuthMode('login');
  };

  const firstName = profileQuery.data?.first_name || customer?.first_name;

  const accountItems = [
    { key: 'email', label: customer?.email ?? displayName ?? '', disabled: true },
    { type: 'divider' },
    { key: 'profile', icon: <UserOutlined />, label: t('profile', 'Profile') },
    // Switch Account hidden for grocery for now.
    // {
    //   key: 'switch_account',
    //   icon: <UserSwitchOutlined />,
    //   label: t('switchAccount', 'Switch Account'),
    // },
    { type: 'divider' },
    { key: 'logout', icon: <LogoutOutlined />, label: t('signOut', 'Sign Out') },
  ];

  const onAccountClick = ({ key }) => {
    if (key === 'profile') navigate('/account');
    if (key === 'orders') navigate('/orders');
    if (key === 'switch_account') openAccountSelector();
    if (key === 'logout') {
      logout();
      navigate('/');
    }
  };

  const closeCart = useCallback(() => setCartOpen(false), []);
  // Checkout opens in a modal over the page (coffee flow); after a login it
  // reopens there instead of routing to /checkout.
  const goToCheckout = () => {
    if (isAuthenticated) {
      openCheckoutModal();
      return;
    }
    setAuthRedirect(CHECKOUT_MODAL_REDIRECT);
    setAuthMode('login');
  };

  // Fetch the checkout code while the cart is open so the modal opens fast.
  useEffect(() => {
    if (cartOpen) loadCheckoutModule();
  }, [cartOpen]);

  const mobileTabs = [
    { key: 'home', to: '/', label: t('home', 'Home'), icon: <HomeOutlined /> },
    {
      key: 'category',
      to: '/catalog?view=categories',
      label: t('category', 'Category'),
      icon: <AppstoreOutlined />,
    },
    { key: 'shop', to: '/catalog', label: t('shop', 'Shop'), icon: <ShoppingOutlined /> },
    { key: 'orders', to: '/orders', label: t('orders', 'Orders'), icon: <ReadOutlined /> },
  ];

  const isTabActive = (tab) => {
    if (tab.key === 'home') return pathname === '/';
    if (tab.key === 'category') return isCatalogPage && isCategoryView;
    if (tab.key === 'shop') return pathname === '/catalog' && !isCategoryView;
    return pathname.startsWith(tab.to);
  };

  const isNavActive = (to) => {
    const [toPath, toQuery = ''] = to.split('?');
    if (toPath !== pathname) return false;
    return toQuery ? search.includes(toQuery) : !search.includes('view=');
  };

  const renderSearchForm = () => {
    if (!isSearchAllowedPage) {
      return <div className="gx-search gx-search--placeholder" aria-hidden />;
    }
    return (
      <form className="gx-search" role="search" onSubmit={onSearchSubmit}>
        <SearchOutlined className="gx-search__icon" aria-hidden />
        <input
          type="search"
          value={searchText}
          onChange={onSearchChange}
          placeholder={t('searchPlaceholder', 'Search for products...')}
          aria-label={t('searchProducts', 'Search products')}
        />
        <button type="submit" className="gx-search__submit" aria-label={t('search', 'Search')}>
          <SearchOutlined aria-hidden />
        </button>
      </form>
    );
  };

  // "My Account / Log in" — icon in a translucent circle over two text lines.
  const accountControl = isAuthenticated ? (
    <Dropdown menu={{ items: accountItems, onClick: onAccountClick }} trigger={['click']}>
      <button type="button" className="gx-hcta" aria-label={t('account', 'Account')}>
        <span className="gx-hcta__icon gx-hcta__icon--avatar">
          <Avatar size={40} style={getAvatarStyles(displayName)} className="gx-account-avatar">
            {displayName[0]?.toUpperCase()}
          </Avatar>
        </span>
        <span className="gx-hcta__text">
          <strong>{t('myAccount', 'My Account')}</strong>
          <small>{displayName}</small>
        </span>
      </button>
    </Dropdown>
  ) : (
    <button
      type="button"
      className="gx-hcta"
      aria-label={t('signIn', 'Log in')}
      onClick={() => setAuthMode('choice')}
    >
      <span className="gx-hcta__icon">
        <UserOutlined />
      </span>
      <span className="gx-hcta__text">
        <strong>{t('myAccount', 'My Account')}</strong>
        <small>{t('logIn', 'Log in')}</small>
      </span>
    </button>
  );

  return (
    <>
      <header ref={headerRef} className={`grocery-header gx-header ${isStuck ? 'is-stuck' : ''}`}>
        <div className="gx-masthead" ref={mastheadRef}>
          <div className="gx-wrap gx-masthead__inner">
            {isMobile ? (
              <button
                type="button"
                className="gx-mloc"
                onClick={openStoreSelector}
                aria-label={`${t('changeStore', 'Change store')}: ${selectedLocationLabel}`}
              >
                <span className="gx-mloc__pin">
                  <LocationPin />
                </span>
                <span className="gx-mloc__text">
                  <small>{t('location', 'Location')}</small>
                  <strong>
                    <span className="gx-mloc__name">{selectedLocationLabel}</span>
                    <DownOutlined className="gx-mloc__caret" />
                  </strong>
                </span>
              </button>
            ) : (
              <div className="gx-masthead__brand">
                <BrandMark logoUrl={logoUrl} appName={appName} />
              </div>
            )}

            {!isMobile ? <div className="gx-search-desktop">{renderSearchForm()}</div> : null}

            <div className="gx-masthead__actions">
              {!isMobile ? (
                <button
                  type="button"
                  className="gx-location"
                  onClick={openStoreSelector}
                  aria-label={`${t('changeStore', 'Change store')}: ${selectedLocationLabel}`}
                >
                  <LocationPin />
                  <strong className="gx-location__name">{selectedLocationLabel}</strong>
                  <DownOutlined className="gx-location__caret" />
                </button>
              ) : null}

              {isSearchAllowedPage && !isCategoryView && !pathname.startsWith('/account') ? (
                <button
                  type="button"
                  className={`gx-hcta gx-search-toggle ${mobileSearchOpen ? 'is-active' : ''}`}
                  aria-label={
                    mobileSearchOpen ? t('closeSearch', 'Close search') : t('search', 'Search')
                  }
                  aria-expanded={mobileSearchOpen}
                  onClick={() => {
                    setMobileSearchOpen((prev) => {
                      const next = !prev;
                      if (next) {
                        setTimeout(() => {
                          headerRef.current?.querySelector('.gx-search-bar-below input')?.focus();
                        }, 50);
                      }
                      return next;
                    });
                  }}
                >
                  <span className="gx-hcta__icon">
                    {mobileSearchOpen ? <CloseOutlined /> : <SearchOutlined />}
                  </span>
                </button>
              ) : null}

              {/* Phones keep favourites in the header; the cart lives in the
                  floating cart bar there. */}
              <Link
                to="/wishlist"
                state={{ refreshFavoritesAt: Date.now() }}
                className="gx-hcta"
                aria-label={t('wishlist', 'Wishlist')}
                title={t('wishlist', 'Wishlist')}
              >
                <span className="gx-hcta__icon">
                  <Badge count={wishlistCount} size="small" showZero>
                    <HeartOutlined />
                  </Badge>
                </span>
              </Link>

              {!isMobile ? accountControl : null}

              {!isMobile ? (
                <button
                  type="button"
                  className="gx-hcta gx-hcta--cart"
                  aria-label={t('openCart', 'Open cart')}
                  onClick={() => setCartOpen(true)}
                >
                  <span className="gx-hcta__icon">
                    <Badge count={lineCount} size="small" showZero>
                      <ShoppingOutlined />
                    </Badge>
                  </span>
                  <span className="gx-hcta__text">
                    <strong>{t('myCart', 'My Cart')}</strong>
                    <small>
                      <Price amount={cart?.grandTotal ?? cart?.subtotal ?? 0} />
                    </small>
                  </span>
                </button>
              ) : null}

              {isMobile ? (
                <button
                  type="button"
                  className="gx-mavatar"
                  aria-label={isAuthenticated ? t('account', 'Account') : t('signIn', 'Log in')}
                  onClick={() => (isAuthenticated ? navigate('/account') : setAuthMode('choice'))}
                >
                  {isAuthenticated ? (
                    <Avatar size={40} style={getAvatarStyles(displayName)}>
                      {displayName[0]?.toUpperCase()}
                    </Avatar>
                  ) : (
                    <UserOutlined />
                  )}
                </button>
              ) : null}
            </div>
          </div>
        </div>

        {/* Mobile (<= 36rem): search bar is always visible directly under masthead (previous behavior) */}
        {isSearchAllowedPage && !isCategoryView && !pathname.startsWith('/account') ? (
          <div className="gx-mobilebar gx-mobilebar--static">{renderSearchForm()}</div>
        ) : null}

        {/* Tablet (36.01rem to 64rem): dropdown search bar toggled by search icon click */}
        {isSearchAllowedPage &&
        !isCategoryView &&
        !pathname.startsWith('/account') &&
        mobileSearchOpen ? (
          <div className="gx-search-bar-below">{renderSearchForm()}</div>
        ) : null}

        {!isMobile ? (
          <nav className="gx-navbar" aria-label={t('mainNavigation', 'Main navigation')}>
            <div className="gx-wrap gx-navbar__inner">
              <ul className="gx-navbar__links">
                <li>
                  <NavLink to="/" end className={() => (pathname === '/' ? 'is-active' : '')}>
                    {t('home', 'Home')}
                  </NavLink>
                </li>
                {primaryNav.map((item) => (
                  <li key={item.to}>
                    <Link
                      to={item.to}
                      className={isNavActive(item.to) ? 'is-active' : ''}
                      onClick={(e) => {
                        if (item.to === '/orders' && !isAuthenticated) {
                          e.preventDefault();
                          requireAuth('/orders');
                        }
                      }}
                    >
                      {t(item.label, item.label)}
                    </Link>
                  </li>
                ))}
              </ul>
              {/* The masthead (and its cart) has scrolled away — keep the cart
                  reachable from the pinned nav bar. */}
              {isStuck ? (
                <button
                  type="button"
                  className="gx-hcta gx-hcta--cart gx-navbar__cart"
                  aria-label={t('openCart', 'Open cart')}
                  onClick={() => setCartOpen(true)}
                >
                  <span className="gx-hcta__icon">
                    <Badge count={lineCount} size="small" showZero>
                      <ShoppingOutlined />
                    </Badge>
                  </span>
                  <span className="gx-hcta__text">
                    <strong>{t('myCart', 'My Cart')}</strong>
                    <small>
                      <Price amount={cart?.grandTotal ?? cart?.subtotal ?? 0} />
                    </small>
                  </span>
                </button>
              ) : null}
            </div>
          </nav>
        ) : null}
      </header>

      {isMobile ? (
        <nav className="grocery-mobile-tabs gx-tabbar" aria-label="Mobile navigation">
          {mobileTabs.map((tab) => (
            <Link
              key={tab.key}
              to={tab.to}
              onClick={(e) => {
                if (tab.to === '/orders' && !isAuthenticated) {
                  e.preventDefault();
                  requireAuth('/orders');
                }
              }}
              className={`gx-tabbar__tab ${isTabActive(tab) ? 'is-active' : ''}`}
            >
              <span className="gx-tabbar__icon">{tab.icon}</span>
              <span className="gx-tabbar__label">{tab.label}</span>
            </Link>
          ))}
          {/* No Account tab: the header avatar opens the account / log in. */}
        </nav>
      ) : null}

      <CartDrawer open={cartOpen} onClose={closeCart} onCheckout={goToCheckout} />

      <StorefrontAuthModal
        open={Boolean(authMode)}
        mode={authMode ?? 'choice'}
        onCancel={() => {
          setAuthMode(null);
          setAuthRedirect(null);
        }}
        onAuthenticated={() => {
          if (authRedirect === CHECKOUT_MODAL_REDIRECT) openCheckoutModal();
          else if (authRedirect) navigate(authRedirect);
          setAuthRedirect(null);
        }}
      />
      <CheckoutModal className="grocery-checkout-modal" />
    </>
  );
}
